/**
 * RiskAssessmentSidebar - Standard 1/3 width sidebar for risk module
 * Shows risk statistics, quick actions, and requirements
 *
 * FT-90: Implements Review Due compliance model for Risk Assessments
 * Formula: (total - overdue - dueSoon) / total × 100
 * Shows: Are Risk Assessments up to date with required periodic reviews?
 *
 * This aligns with ISM Code and UK Workboat Code requirements for
 * documented, current risk assessments that are reviewed periodically.
 *
 * @module components/RiskAssessmentSidebar
 */

import React from 'react';
import {
  AlertTriangle,
  FileText,
  Shield,
  Settings,
  Download,
  TrendingUp,
  Zap,
  History,
  Clock,
  Calendar
} from 'lucide-react';

/**
 * RiskAssessmentSidebar Component
 * @param {Object} props - Component props
 * @param {Object} props.stats - Risk statistics
 * @param {Function} props.onCreateAssessment - Handler for Create button
 * @param {Function} props.onExportRegister - Handler for Export button
 * @param {Function} props.onExportHighRisk - Handler for High Risk Report button
 * @param {Function} props.onOpenSettings - Handler for Settings button
 * @param {Function} props.onViewHistory - Handler for View History button
 * @param {Function} props.onViewReviewSchedule - Handler for Review Schedule button
 * @param {number} props.overdueCount - Number of overdue assessments for badge
 */
export const RiskAssessmentSidebar = ({
  stats = {},
  onCreateAssessment,
  onExportRegister,
  onExportHighRisk,
  onOpenSettings,
  onViewHistory,
  onViewReviewSchedule,
  overdueCount = 0
}) => {
  const {
    total = 0,
    extreme = 0,
    high = 0,
    medium = 0,
    low = 0,
    reviewDue = 0,
    overdue = 0
  } = stats;

  const getRiskColor = (level) => {
    if (level === 'extreme') return 'text-red-600';
    if (level === 'high') return 'text-orange-600';
    if (level === 'medium') return 'text-yellow-600';
    return 'text-green-600';
  };

  const getComplianceColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-500';
    if (percentage >= 70) return 'bg-yellow-500';
    if (percentage >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };

  // FT-90: Review Due compliance model for Risk Assessments
  // Formula: (total - overdue - dueSoon) / total × 100
  // Shows: Are Risk Assessments up to date with required periodic reviews?
  // Both overdue AND due soon affect compliance - they're separate states
  const compliancePercentage = total > 0
    ? Math.round(((total - overdue - reviewDue) / total) * 100)
    : 100;

  return (
    <div className="space-y-6">
      {/* Risk Summary Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-white" />
            Risk Summary
          </h3>
        </div>

        <div className="p-6">
          {/* Review Due Stats - Show prominently at top if any */}
          {(overdue > 0 || reviewDue > 0) && (
            <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
              {overdue > 0 && (
                <div className="flex items-center justify-between py-2 bg-red-50 rounded-lg px-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-700">Overdue Reviews</span>
                  </div>
                  <span className="text-sm font-semibold text-red-600">{overdue}</span>
                </div>
              )}
              {reviewDue > 0 && (
                <div className="flex items-center justify-between py-2 bg-amber-50 rounded-lg px-3">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-amber-700">Due Soon</span>
                  </div>
                  <span className="text-sm font-semibold text-amber-600">{reviewDue}</span>
                </div>
              )}
            </div>
          )}

          {/* Risk Distribution */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Extreme Risks</span>
              <span className={`text-sm font-semibold ${getRiskColor('extreme')}`}>
                {extreme}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">High Risks</span>
              <span className={`text-sm font-semibold ${getRiskColor('high')}`}>
                {high}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Medium Risks</span>
              <span className={`text-sm font-semibold ${getRiskColor('medium')}`}>
                {medium}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Low Risks</span>
              <span className={`text-sm font-semibold ${getRiskColor('low')}`}>
                {low}
              </span>
            </div>
          </div>

          {/* Total Assessments */}
          <div className="pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Total Active</span>
              <span className="text-sm font-bold text-gray-900">{total}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Review Status Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-white" />
            Review Compliance
          </h3>
        </div>

        <div className="p-6">
          {/* Progress Bar */}
          <div className="mb-2">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Assessments Reviewed</span>
              <span className="font-medium">{compliancePercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getComplianceColor(compliancePercentage)}`}
                style={{ width: `${compliancePercentage}%` }}
              />
            </div>
          </div>

          {(reviewDue > 0 || overdue > 0) && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  {reviewDue || overdue} assessment{(reviewDue || overdue) !== 1 ? 's' : ''} due for review
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-700 px-5 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-white" />
            Quick Actions
          </h3>
        </div>
        <div className="p-6 space-y-2">
          <button
            onClick={onCreateAssessment}
            className="w-full flex items-center justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 transition-colors"
          >
            <FileText className="h-4 w-4 mr-2" />
            Create Assessment
          </button>

          <button
            onClick={onExportRegister}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Register
          </button>

          {/* FT-128: High Risk Report button removed - redundant with Export Register */}

          <button
            onClick={onViewHistory}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <History className="h-4 w-4 mr-2" />
            View Audit Log
          </button>

          <button
            onClick={onViewReviewSchedule}
            className={`w-full flex items-center justify-between px-4 py-2 border text-sm font-medium rounded-md transition-colors ${
              overdueCount > 0
                ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-2" />
              Review Schedule
            </span>
            {overdueCount > 0 && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {overdueCount}
              </span>
            )}
          </button>

          <button
            onClick={onOpenSettings}
            className="w-full flex items-center justify-start px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </button>
        </div>
      </div>

      {/* Requirements Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-5 w-5 text-blue-600" />
          <h3 className="text-sm font-medium text-blue-900">Regulatory Requirements</h3>
        </div>
        <ul className="space-y-2 text-sm text-blue-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>ISM Code requires documented risk assessments</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>UK Workboat Code Chapter 7 safety procedures</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>COSWP Chapter 1 risk assessment guidance</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-500 mt-0.5">•</span>
            <span>Review assessments annually (or after incidents/changes)</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RiskAssessmentSidebar;
